import { Assets, Texture } from "pixi.js";
import { isWallTile, tileAt, tileKind, type LevelData } from "./level";

export type PlayerDirection = "down" | "up" | "left" | "right";
export type PlayerAnimationState = "idle" | "moving";
export type PlayerAnimations = Record<PlayerDirection, Record<PlayerAnimationState, Texture[]>>;

const textureUrls = {
  floor1: "/assets/grafik/bereiche/Bereich1/Bereich1_Weg_Grid_A_12.png",
  floor2: "/assets/grafik/bereiche/Bereich2/floor2.png",
  floor2Alt: "/assets/grafik/bereiche/Bereich2/floor3.png",
  floor3: "/assets/grafik/bereiche/Bereich3/floor.png",
  // Use already bundled textures as safe fallbacks so mined tiles never resolve to missing assets.
  mined1: "/assets/grafik/bereiche/Bereich2/stonebrick_cracked.png",
  mined2: "/assets/grafik/bereiche/Bereich2/stonebrick_cracked.png",
  mined3: "/assets/grafik/bereiche/Bereich3/red_nether_broken.png",
  wall1: "/assets/grafik/bereiche/Bereich1/leaves_birch_colored.png",
  wall2: "/assets/grafik/bereiche/Bereich2/stonebrick.png",
  wall2Carved: "/assets/grafik/bereiche/Bereich2/stonebrick_carved.png",
  wall2Cracked: "/assets/grafik/bereiche/Bereich2/stonebrick_cracked.png",
  wall2Mossy: "/assets/grafik/bereiche/Bereich2/stonebrick_mossy.png",
  wall3: "/assets/grafik/bereiche/Bereich3/red_nether_brick.png",
  outerWall: "/assets/grafik/WWW/Wall_1.png",
  knotWall: "/assets/grafik/knotenpunkte/Knotenpunkt1/wall.png",
  knotFloor: "/assets/grafik/knotenpunkte/Knotenpunkt1/floor.png",
  knotWall2: "/assets/grafik/knotenpunkte/Knotenpunkt2/wall.png",
  knotFloor2: "/assets/grafik/knotenpunkte/Knotenpunkt2/floor.png",
  knotWall3: "/assets/grafik/knotenpunkte/Knotenpunkt3/wall.png",
  knotFloor3: "/assets/grafik/knotenpunkte/Knotenpunkt3/floor.png",
  knotWall4: "/assets/grafik/knotenpunkte/Knotenpunkt4/wall.png",
  knotFloor4: "/assets/grafik/knotenpunkte/Knotenpunkt4/floor.png",
  knotWall5: "/assets/grafik/knotenpunkte/Knotenpunkt5/wall.png",
  knotFloor5: "/assets/grafik/knotenpunkte/Knotenpunkt5/floor.png",
  knotWall6: "/assets/grafik/knotenpunkte/Knotenpunkt6/wall.png",
  knotFloor6: "/assets/grafik/knotenpunkte/Knotenpunkt6/floor.png",
  start: "/assets/grafik/bereiche/Bereich1/Bereich1_Weg_Grid_A_12.png",
  finish: "/assets/grafik/bereiche/Victory_Tile.png",
  key2: "/assets/grafik/items/KeyFolder/KY2.png",
  key3: "/assets/grafik/items/KeyFolder/KY3.png",
  key4: "/assets/grafik/items/KeyFolder/KY4.png",
  key5: "/assets/grafik/items/KeyFolder/KY5.png",
  key6: "/assets/grafik/items/KeyFolder/KY6.png",
  coin: "/assets/grafik/items/emerald_ground.png",
  dealer: "/assets/grafik/weiteres/Dealer.png",
  dealerWagon: "/assets/grafik/weiteres/Dealer_wagon.png",
  door: "/assets/animation/doorAnimation/1/sprite1.png",
  fuel: "/assets/grafik/items/Fire.png",
  fuelStation: "/assets/grafik/items/CampFire.png",
  range: "/assets/grafik/items/scroll_ground.png",
  sonar: "/assets/grafik/items/Sonar_ground.png",
  pickaxe: "/assets/grafik/items/Pickaxe.png",
  lamp: "/assets/grafik/bereiche/Fackel.png",
  lamp1: "/assets/grafik/bereiche/Bereich1/Lamp_1.png",
  lamp2: "/assets/grafik/bereiche/Bereich2/redstone_lamp_on.png",
  lamp3: "/assets/grafik/bereiche/Bereich3/red_nether_brick_lamp.png",
  player: "/assets/animation/AnimationPlayer/downIdle/smalllinks_11.png",
} as const;

export const keychainUrls = [
  "/assets/grafik/gui/KeyChain/0.png",
  "/assets/grafik/gui/KeyChain/1.png",
  "/assets/grafik/gui/KeyChain/2.png",
  "/assets/grafik/gui/KeyChain/3.png",
  "/assets/grafik/gui/KeyChain/4.png",
  "/assets/grafik/gui/KeyChain/5.png",
] as const;

export const uiIconUrls = {
  range: "/assets/grafik/items/scroll_inventory.png",
  sonar: "/assets/grafik/items/Sonar_inventory.png",
  pickaxe: "/assets/grafik/items/Pickaxe.png",
  compass: "/assets/grafik/items/Compass_inventory.png",
  compassBody: "/assets/grafik/items/Compass_body.png",
  compassNeedle: "/assets/grafik/items/Compass_needle.png",
  betterTank: "/assets/grafik/gui/BetterTank.png",
  advancedView: "/assets/grafik/gui/AdvancedView.png",
  gemMagnet: "/assets/grafik/gui/Gem_Magnet.png",
  easterEgg: "/assets/grafik/items/EasterEgg.png",
  fuelStation: "/assets/grafik/items/CampFire.png",
  coin: "/assets/grafik/items/emerald_inventory.png",
  fuel: "/assets/grafik/items/Fire.png",
  fuelTankEmpty: "/assets/grafik/gui/FuelContainer/Empty.png",
  fuelTankFull: "/assets/grafik/gui/Füllstand_full.png",
  speechBubble: "/assets/grafik/gui/speechBubble.png",
  easterQr: "/assets/grafik/weiteres/QR.png",
} as const;

const doorAnimationUrls: Record<number, string[]> = {
  1: numberedUrls("/assets/animation/doorAnimation/1", "sprite", 1, 6),
  2: [
    "/assets/animation/doorAnimation/2/sprite0.png",
    "/assets/animation/doorAnimation/2/sprite1.png",
    "/assets/animation/doorAnimation/2/sprite2.png",
    "/assets/animation/doorAnimation/2/sprite3.png",
    "/assets/animation/doorAnimation/2/sprite5.png",
    "/assets/animation/doorAnimation/2/sprite6.png",
  ],
  3: numberedUrls("/assets/animation/doorAnimation/3", "sprite", 0, 5),
  4: numberedUrls("/assets/animation/doorAnimation/4", "sprite", 0, 5),
  5: numberedUrls("/assets/animation/doorAnimation/5", "sprite", 0, 5),
};

const miningAnimationUrls = numberedUrls("/assets/animation/MiningAnimation", "sprite_", 0, 5);
const domUiPreloadUrls = [
  uiIconUrls.speechBubble,
  "/assets/grafik/gui/Dealerbackground.png",
  uiIconUrls.easterQr,
] as const;

const playerAnimationUrls: Record<PlayerDirection, Record<PlayerAnimationState, string[]>> = {
  down: {
    idle: [
      "/assets/animation/AnimationPlayer/downIdle/smalllinks_11.png",
      "/assets/animation/AnimationPlayer/downIdle/smalllinks_13.png",
    ],
    moving: numberedUrls("/assets/animation/AnimationPlayer/downMoving", "smalllinks_", 9, 16),
  },
  up: {
    idle: [
      "/assets/animation/AnimationPlayer/upIdle/smalllinks_26.png",
      "/assets/animation/AnimationPlayer/upIdle/smalllinks_27.png",
    ],
    moving: numberedUrls("/assets/animation/AnimationPlayer/upMoving", "smalllinks_", 25, 32),
  },
  left: {
    idle: [
      "/assets/animation/AnimationPlayer/leftIdle/smalllinks_17.png",
      "/assets/animation/AnimationPlayer/leftIdle/smalllinks_21.png",
    ],
    moving: numberedUrls("/assets/animation/AnimationPlayer/leftMoving", "smalllinks_", 17, 24),
  },
  right: {
    idle: [
      "/assets/animation/AnimationPlayer/rightIdle/smalllinks_1.png",
      "/assets/animation/AnimationPlayer/rightIdle/smalllinks_5.png",
    ],
    moving: numberedUrls("/assets/animation/AnimationPlayer/rightMoving", "smalllinks_", 1, 8),
  },
};

export type TextureKey = keyof typeof textureUrls;
export type TextureAtlas = Record<TextureKey, Texture> & {
  playerAnimations: PlayerAnimations;
  doorFrames: Record<number, Texture[]>;
  miningFrames: Texture[];
};

export async function loadTextures(): Promise<TextureAtlas> {
  const baseEntries = Object.entries(textureUrls) as Array<[TextureKey, string]>;
  const animationEntries = Object.values(playerAnimationUrls)
    .flatMap((states) => Object.values(states))
    .flat();
  const doorEntries = Object.values(doorAnimationUrls).flat();
  const requiredUrls = [
    ...new Set([
      ...baseEntries.map(([, url]) => url),
      ...animationEntries,
      ...doorEntries,
      ...miningAnimationUrls,
    ]),
  ];
  const loaded = await Assets.load(requiredUrls);
  // Best-effort preload for DOM-only visuals. Missing optional files must not block startup.
  await Promise.allSettled(
    [...new Set(domUiPreloadUrls)].map(async (url) => {
      try {
        await Assets.load(url);
      } catch (error) {
        console.warn(`[assets] optional preload failed: ${url}`, error);
      }
    }),
  );
  const atlas = {} as TextureAtlas;

  for (const [key, url] of baseEntries) {
    atlas[key] = loaded[url] as Texture;
  }

  atlas.playerAnimations = {
    down: {
      idle: texturesForUrls(loaded, playerAnimationUrls.down.idle),
      moving: texturesForUrls(loaded, playerAnimationUrls.down.moving),
    },
    up: {
      idle: texturesForUrls(loaded, playerAnimationUrls.up.idle),
      moving: texturesForUrls(loaded, playerAnimationUrls.up.moving),
    },
    left: {
      idle: texturesForUrls(loaded, playerAnimationUrls.left.idle),
      moving: texturesForUrls(loaded, playerAnimationUrls.left.moving),
    },
    right: {
      idle: texturesForUrls(loaded, playerAnimationUrls.right.idle),
      moving: texturesForUrls(loaded, playerAnimationUrls.right.moving),
    },
  };
  atlas.doorFrames = Object.fromEntries(
    Object.entries(doorAnimationUrls).map(([key, urlsForDoor]) => [
      Number(key),
      texturesForUrls(loaded, urlsForDoor),
    ]),
  );
  atlas.miningFrames = texturesForUrls(loaded, miningAnimationUrls);

  return atlas;
}

export function textureKeyForTile(tile: string, x = 0, y = 0, level?: LevelData): TextureKey {
  const normalized = tile.toUpperCase();
  const kind = tileKind(tile);
  if (kind === "key") {
    const regionNumber = Math.max(1, (numericSuffix(tile) ?? 2) - 1);
    if (regionNumber >= 3) return "floor3";
    if (regionNumber === 2) return "floor2";
    return "floor1";
  }
  if (kind === "dealer") {
    return knotFloorKey(numericSuffix(tile) ?? 1);
  }
  if (kind === "door" || kind === "fakeDoor") {
    return knotFloorKey(doorFloorRegion(tile, x, y, level));
  }
  if (kind === "finish") return "finish";
  if (kind === "start") return "start";
  if (normalized.startsWith("M_")) {
    const region = numericSuffix(tile) ?? 1;
    if (region >= 3) return "mined3";
    if (region === 2) return "mined2";
    return "mined1";
  }
  if (isWallTile(tile)) {
    if (normalized === "WWW") return "outerWall";
    if (normalized.startsWith("KW")) return knotWallKey(numericSuffix(tile) ?? 1);
    if (normalized.endsWith("3")) return "wall3";
    if (normalized.endsWith("2")) return ["wall2", "wall2Carved", "wall2Cracked", "wall2Mossy"][Math.abs(x * 31 + y * 17) % 4] as TextureKey;
    return "wall1";
  }
  if (normalized.startsWith("K_")) return knotFloorKey(numericSuffix(tile) ?? 1);
  if (normalized.endsWith("3")) return "floor3";
  if (normalized.endsWith("2")) return Math.abs(x + y) % 2 === 0 ? "floor2" : "floor2Alt";
  return "floor1";
}

export function keyTextureKeyForTile(tile: string): TextureKey {
  const keyNumber = numericSuffix(tile);
  if (keyNumber === 3) return "key3";
  if (keyNumber === 4) return "key4";
  if (keyNumber === 5) return "key5";
  if (keyNumber === 6) return "key6";
  return "key2";
}

export function itemTextureKeyForType(itemType: string | undefined): TextureKey {
  if (itemType === "coin") return "coin";
  if (itemType === "range") return "range";
  if (itemType === "sonar") return "sonar";
  if (itemType === "pickaxe") return "pickaxe";
  if (itemType === "fuelStation") return "fuelStation";
  return "fuel";
}

export function lampTextureKeyForTile(tile: string): TextureKey {
  const region = numericSuffix(tile) ?? 1;
  if (region === 1) return "lamp1";
  if (region === 2) return "lamp2";
  if (region === 3) return "lamp3";
  return "lamp";
}

function knotFloorKey(region: number): TextureKey {
  if (region === 2) return "knotFloor2";
  if (region === 3) return "knotFloor3";
  if (region === 4) return "knotFloor4";
  if (region === 5) return "knotFloor5";
  if (region >= 6) return "knotFloor6";
  return "knotFloor";
}

function knotWallKey(region: number): TextureKey {
  if (region === 2) return "knotWall2";
  if (region === 3) return "knotWall3";
  if (region === 4) return "knotWall4";
  if (region === 5) return "knotWall5";
  if (region >= 6) return "knotWall6";
  return "knotWall";
}

function wallTextureForRegion(region: number, x: number, y: number): TextureKey {
  if (region >= 3) return "wall3";
  if (region === 2) {
    return ["wall2", "wall2Carved", "wall2Cracked", "wall2Mossy"][Math.abs(x * 31 + y * 17) % 4] as TextureKey;
  }
  return "wall1";
}

function numberedUrls(basePath: string, prefix: string, start: number, end: number): string[] {
  const urls = [];
  for (let index = start; index <= end; index += 1) {
    urls.push(`${basePath}/${prefix}${index}.png`);
  }
  return urls;
}

function texturesForUrls(loaded: Record<string, unknown>, urls: string[]): Texture[] {
  return urls.map((url) => loaded[url] as Texture).filter(Boolean);
}

function numericSuffix(value: string): number | null {
  const match = value.match(/\d+/);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : null;
}

function doorFloorRegion(tile: string, x: number, y: number, level?: LevelData): number {
  const fallbackRegion = numericSuffix(tile) ?? 1;
  if (!level) return fallbackRegion;

  const orientation = tile.slice(1, 2).toUpperCase();
  const innerTile =
    orientation === "U"
      ? tileAt(level, x, y + 1)
      : orientation === "D"
        ? tileAt(level, x, y - 1)
        : orientation === "L"
          ? tileAt(level, x + 1, y)
          : orientation === "R"
            ? tileAt(level, x - 1, y)
            : "";
  const innerRegion = knotRegionForDoorFloorTile(innerTile);
  if (innerRegion !== null) return innerRegion;

  const neighbors = [
    tileAt(level, x, y - 1),
    tileAt(level, x, y + 1),
    tileAt(level, x - 1, y),
    tileAt(level, x + 1, y),
  ];
  for (const neighbor of neighbors) {
    const region = knotRegionForDoorFloorTile(neighbor);
    if (region !== null) return region;
  }
  return fallbackRegion;
}

function knotRegionForDoorFloorTile(tile: string): number | null {
  if (!(tile.startsWith("K_") || tile.startsWith("KW"))) return null;
  return numericSuffix(tile);
}
